import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Select from "react-select";
import { getCNVTranscripts } from "../../../state/selectors/CNVs.selectors";
import { getVarsomeHost } from "../../../state/selectors/general.selectors";
import withErrorGuard from "../../../HOC/withErrorGuard";
import { Pagination } from "../../../components/atoms";
import GeneWithRefGenomeLink from "../../../common/helpers/GeneWithRefGenomeLink";
import ComponentPanel from "../../../components/atoms/ComponentPanel/ComponentPanel";

const CNVTranscripts = ({ transcripts, hostUrl }) => {
  const [geneTypeFilter, setGeneTypeFilter] = useState();
  const pageSizeOptions = [10, 20, 30, 50];
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [totalTranscripts, setTotalTranscripts] = useState(
    transcripts ? transcripts.length : 0
  );
  const [transciptsGeneTypeCopy, setTransciptsGeneTypeCopy] = useState(
    transcripts || []
  );
  const [transcriptsGeneNameCopy, setTranscriptsGeneNameCopy] = useState([]);
  const [selectedGeneNameValue, setSelectedGeneNameValue] = useState(null);
  const [page, setPage] = useState(1);
  const [visibleTranscripts, setVisibleTranscripts] = useState([]);
  const [options, setOptions] = useState([]);

  const filterTypeOptions = [
    {
      label: "All",
      value: "All",
    },
    {
      label: "Coding Genes",
      value: "Coding Genes",
    },
    {
      label: "Non-Coding Genes",
      value: "Non-Coding Genes",
    },
  ];

  const filterOptions = {
    "Coding Genes": true,
    "Non-Coding Genes": false,
  };
  const onPageSizeUpdate = useCallback((pSize) => {
    setPage(1);
    setPageSize(pSize);
  }, []);

  const unique = (value, index, self) => {
    return self.indexOf(value) === index;
  };

  const getGenesFromTranscirpts = (currentTranscripts) => {
    setOptions(
      currentTranscripts
        .map((value) => value.gene)
        .filter(unique)
        .map((gene) => {
          return { value: gene, label: gene };
        })
    );
  };

  useEffect(() => {
    setGeneTypeFilter("Coding Genes");
  }, []);

  useEffect(() => {
    if (!transcripts) return;
    setPage(1);
    if (geneTypeFilter === "All") {
      setTransciptsGeneTypeCopy(transcripts);
      setTotalTranscripts(transcripts.length);
      getGenesFromTranscirpts(transcripts);
      setSelectedGeneNameValue(null);
      return;
    }
    const res = transcripts.filter((elem) => {
      return elem.coding_impact === filterOptions[geneTypeFilter];
    });
    setTransciptsGeneTypeCopy(res);
    setTotalTranscripts(res.length);
    getGenesFromTranscirpts(res);
    setSelectedGeneNameValue(null);
  }, [geneTypeFilter]);

  useEffect(() => {
    if (!transcripts) return;
    const items = !transcriptsGeneNameCopy.length
      ? transciptsGeneTypeCopy.slice(pageSize * (page - 1), pageSize * page)
      : transcriptsGeneNameCopy.slice(pageSize * (page - 1), pageSize * page);
    setVisibleTranscripts([...items]);
  }, [transciptsGeneTypeCopy, page, pageSize, transcriptsGeneNameCopy]);

  const updatePage = useCallback((pageNum) => {
    setPage(pageNum);
  }, []);

  useEffect(() => {
    if (!selectedGeneNameValue) {
      getGenesFromTranscirpts(transciptsGeneTypeCopy);
      setTranscriptsGeneNameCopy([]);
      setTotalTranscripts(transciptsGeneTypeCopy.length);
      return;
    }
    const result = transciptsGeneTypeCopy.filter((elem) => {
      return elem.gene === selectedGeneNameValue;
    });
    setTranscriptsGeneNameCopy(result);
    setTotalTranscripts(result.length);
  }, [selectedGeneNameValue]);

  return (
    <div className="varsome-result-fixed">
      <div className="varsome-container varsome-container__basic">
        <ComponentPanel
          componentName="CNV Transcripts Component"
          title="Transcripts"
          content={
            <div className="varsome-inner-container">
              {!transcripts || !transcripts.length ? (
                <div className="tw-text-center tw-text-base">
                  No transcripts found
                </div>
              ) : (
                <>
                  <div className="tw-mb-4">
                    <div className="tw-mr-1 tw-text-coolGray-400 tw-text-sm">
                      FILTER RESULTS
                    </div>
                  </div>
                  <div className="tw-flex tw-flex-row tw-mb-6">
                    <Select
                      className="tw-w-48"
                      menuPortalTarget={document.body}
                      options={filterTypeOptions}
                      value={geneTypeFilter ? { label: geneTypeFilter } : ""}
                      onChange={(e) => setGeneTypeFilter(e?.value)}
                    />
                    <Select
                      menuPortalTarget={document.body}
                      className="tw-w-48 tw-ml-4"
                      options={options}
                      placeholder="Select a gene..."
                      value={
                        selectedGeneNameValue
                          ? { label: selectedGeneNameValue }
                          : ""
                      }
                      onChange={(e) =>
                        setSelectedGeneNameValue(e?.value || null)
                      }
                      isClearable={selectedGeneNameValue}
                    />
                  </div>
                  {visibleTranscripts.length > 0 ? (
                    <table className="varsome-table">
                      <thead>
                        <tr>
                          <th className="tw-font-bold">Transcript</th>
                          <th className="tw-font-bold">Gene</th>
                          <th className="tw-font-bold">Overlap</th>
                          <th className="tw-font-bold">Function</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleTranscripts.map((element, index) => {
                          return (
                            <tr
                              key={`${element.transcript}-${element.gene}-${index}`}
                              className=""
                            >
                              <td>
                                <a
                                  href={`${hostUrl}/transcript/hg19/${element.transcript}`}
                                  target="_blank"
                                >
                                  {element.transcript}
                                </a>
                              </td>
                              <td>
                                <GeneWithRefGenomeLink
                                  href={`${hostUrl}/gene/${element.gene}`}
                                  target="_blank"
                                >
                                  {element.gene}
                                </GeneWithRefGenomeLink>
                              </td>
                              <td>{element.overlap}</td>
                              <td>
                                {element.coding_impact === true
                                  ? "Coding"
                                  : "Non-Coding"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="tw-text-center tw-text-base">
                      <b>No results found</b>
                    </div>
                  )}
                </>
              )}
              <Pagination
                currentPage={page}
                totalResults={totalTranscripts}
                pageSize={pageSize}
                onPageChange={updatePage}
                onPageSizeChange={onPageSizeUpdate}
                pageSizeOptions={pageSizeOptions}
              />
            </div>
          }
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  transcripts: getCNVTranscripts(state),
  hostUrl: getVarsomeHost(state),
});

CNVTranscripts.propTypes = {
  transcripts: PropTypes.arrayOf(
    PropTypes.shape({
      coding_impact: PropTypes.bool.isRequired,
      overlap: PropTypes.string.isRequired,
      gene: PropTypes.string.isRequired,
      transcript: PropTypes.string.isRequired,
      gene_id: PropTypes.number.isRequired,
    })
  ),
  hostUrl: PropTypes.string.isRequired,
};
CNVTranscripts.defaultProps = {
  transcripts: [],
};

export default connect(mapStateToProps)(
  withErrorGuard(CNVTranscripts, "CNVTranscripts", "CNV Transcripts")
);
